/**
 * Web hook for the `recentActivity` resolver — same query mobile
 * uses (`apps/mobile/lib/graphql/operations.ts:RECENT_ACTIVITY`).
 * Server-side dedupes per-video feedback, filters corrective
 * actions to TAKEN + DISMISSED only, and sorts by timestamp.
 *
 * The web `Your votes` and `Actions taken` pages both read this
 * single endpoint and split client-side by `tone`:
 *   - votes  = tone in (approved, blocked)
 *   - actions = tone === 'ai'
 */
import {ApolloClient, gql, NormalizedCacheObject, useQuery} from '@apollo/client';

export interface ActivityRow {
  id: string;
  tone: 'approved' | 'blocked' | 'ai';
  text: string;
  timestamp: number;
  videoId: string | null;
  videoTitle: string | null;
  channelName: string | null;
  thumbnailUrl: string | null;
  actionId: string | null;
  actionStatus: string | null;
  actionType: string | null;
}

export const QUERY_RECENT_ACTIVITY = gql`
  query RecentActivity($profileId: String!, $limit: Int) {
    recentActivity(profileId: $profileId, limit: $limit) {
      id
      tone
      text
      timestamp
      videoId
      videoTitle
      channelName
      thumbnailUrl
      actionId
      actionStatus
      actionType
    }
  }
`;

export function useRecentActivity(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
  limit: number = 50,
) {
  return useQuery<{recentActivity: ActivityRow[]}>(QUERY_RECENT_ACTIVITY, {
    client,
    variables: {profileId, limit},
    skip: !profileId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
}
